/**
 * UsageAlertBanner — sticky banner surfacing BE-side usage alerts.
 *
 * v1 reference: ``app/components/billing/UsageAlertBanner.tsx``. Reads from
 * ``GET /billing/usage/alerts`` (already wired in
 * ``@ando/core/api/endpoints/billing.ts``). Banner only renders when at
 * least one alert returns ``severity: "warning"`` or ``"critical"``.
 *
 * UX:
 *  - Sticky top inside the BillingSettings scroll area so it persists as
 *    the user scrolls past the plan card / usage meters.
 *  - Dismissible with ``X``. Dismissal stored in ``sessionStorage`` keyed
 *    by ``metric`` so a "muted" alert returns on the next session — we
 *    do not lose user attention permanently.
 *  - Severity tone: warning (yellow) vs critical (red). Critical never
 *    auto-hides — only manual dismiss.
 */

import { AlertTriangle, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import { getBillingUsageAlerts, type BillingUsageAlert } from "@ando/core";
import { cn } from "@ando/ui";

const DISMISS_KEY = "ando.billing.usage-alerts.dismissed.v1";

function loadDismissed(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.sessionStorage.getItem(DISMISS_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}
function saveDismissed(s: Set<string>): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(DISMISS_KEY, JSON.stringify([...s]));
  } catch {
    // sessionStorage full or disabled — degrade silently
  }
}

export function UsageAlertBanner(): React.ReactElement | null {
  const { i18n } = useLingui();
  const query = useQuery({
    queryKey: ["billing", "usage-alerts"],
    queryFn: () => getBillingUsageAlerts(),
    staleTime: 60_000,
  });
  const [dismissed, setDismissed] = useState<Set<string>>(loadDismissed);

  const alerts = (query.data?.alerts ?? []).filter(
    (a: BillingUsageAlert) => !dismissed.has(a.metric),
  );

  useEffect(() => {
    saveDismissed(dismissed);
  }, [dismissed]);

  if (alerts.length === 0) return null;

  // Take the most severe alert as the banner driver — critical wins over
  // warning. Other alerts are listed inline below the title.
  const sorted = [...alerts].sort((a, b) =>
    a.severity === "critical" && b.severity !== "critical" ? -1 : 1,
  );
  const head = sorted[0];
  if (!head) return null;
  const isCritical = head.severity === "critical";

  return (
    <div
      role="alert"
      className={cn(
        "sticky top-0 z-10 flex items-start gap-2.5 rounded-md border px-3 py-2.5",
        isCritical
          ? "border-error/30 bg-error/10 text-error"
          : "border-amber-500/30 bg-amber-500/10 text-amber-900",
      )}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-[12px]">{head.message}</p>
        <p className="mt-0.5 text-[11px] tabular-nums opacity-80">
          {head.metric} · {head.current.toLocaleString()} / {head.limit.toLocaleString()} ·{" "}
          {head.percentage.toFixed(0)}%
        </p>
        {sorted.length > 1 ? (
          <ul className="mt-1 space-y-0.5">
            {sorted.slice(1).map((a) => (
              <li key={a.metric} className="text-[11px] opacity-80">
                <span className="font-medium">{a.metric}</span> · {a.percentage.toFixed(0)}%
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => {
          const next = new Set(dismissed);
          for (const a of alerts) next.add(a.metric);
          setDismissed(next);
        }}
        className={cn(
          "shrink-0 rounded p-1 transition-colors",
          "hover:bg-black/[0.06]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
          isCritical ? "focus-visible:ring-error" : "focus-visible:ring-amber-500",
        )}
        aria-label={i18n._(msg`Chiudi avviso`)}
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}
UsageAlertBanner.displayName = "UsageAlertBanner";
